'use client'

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeStore } from '@/lib/stores/theme-store'
import { cn } from '@/lib/cn'
import { LoginHeader } from './login-header'
import { CenteredLayout } from './layouts/centered-layout'
import { LeftLayout } from './layouts/left-layout'
import { RightLayout } from './layouts/right-layout'
import { RightWithVideoLayout } from './layouts/right-with-video-layout'

interface LoginLayoutProps {
  children: React.ReactNode
  title?: string
  description?: string
  className?: string
}

export function LoginLayout({
  children,
  className
}: LoginLayoutProps) {
  const { t } = useTranslation(['auth'])
  const { currentTheme } = useThemeStore()
  const [isMobile, setIsMobile] = useState(false)

  const titleShowcase = t('auth:login.title')
  const brandName = currentTheme?.title || 'Mentorfy'

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Detectar mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const getBackgroundStyle = (): React.CSSProperties => {
    const bgImage = getComputedStyle(document.documentElement).getPropertyValue('--login-body-background-image')?.trim()

    if (bgImage && bgImage !== 'none' && bgImage !== '') {
      return {
        backgroundColor: 'var(--login-body-bg)',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'var(--login-body-background-fill, cover)',
        backgroundPosition: 'var(--login-body-background-position, center)',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'var(--login-body-background-attachment, scroll)',
      }
    }

    return {
      backgroundColor: 'var(--login-body-bg)',
    }
  }

  // Determinar qual layout usar baseado no modelVideo.model
  const getLayoutComponent = () => {
    // Obter o modelo do tema
    const model = Number(currentTheme?.home?.branding?.modelVideo?.model ?? 0)
    const videoUrl = currentTheme?.home?.branding?.modelVideo?.url || ''
    const banners = currentTheme?.home?.branding?.modelVideo?.banners

    // Em mobile, sempre centralizado (mas com vídeo se existir)
    if (isMobile) {
      return (
        <CenteredLayout titleShowcase={titleShowcase} brandName={brandName} videoUrl={videoUrl}>
          {children}
        </CenteredLayout>
      )
    }

    switch (model) {
      case 0:
        // Centralizado
        return (
          <CenteredLayout titleShowcase={titleShowcase} brandName={brandName}>
            {children}
          </CenteredLayout>
        )

      case 1:
        // À esquerda
        return (
          <LeftLayout
            titleShowcase={titleShowcase}
            brandName={brandName}
            banners={banners}
          >
            {children}
          </LeftLayout>
        )

      case 2:
        // À direita
        return (
          <RightLayout
            titleShowcase={titleShowcase}
            brandName={brandName}
            banners={banners}
          >
            {children}
          </RightLayout>
        )

      case 3:
        // À direita com vídeo no topo
        return (
          <RightWithVideoLayout
            titleShowcase={titleShowcase}
            brandName={brandName}
            videoUrl={videoUrl}
            banners={banners}
          >
            {children}
          </RightWithVideoLayout>
        )

      case 4:
        // Form à esquerda, sem vitrine de capas
        return (
          <LeftLayout
            titleShowcase={titleShowcase}
            brandName={brandName}
            banners={banners}
            showShowcase={false}
          >
            {children}
          </LeftLayout>
        )

      case 5:
        // Form à direita, sem vitrine de capas
        return (
          <RightLayout
            titleShowcase={titleShowcase}
            brandName={brandName}
            banners={banners}
            showShowcase={false}
          >
            {children}
          </RightLayout>
        )

      default:
        // Fallback para centralizado
        return (
          <CenteredLayout titleShowcase={titleShowcase} brandName={brandName}>
            {children}
          </CenteredLayout>
        )
    }
  }

  return (
    <div
      className={cn('h-screen overflow-hidden', className)}
      style={getBackgroundStyle()}
    >
      {/* Header com logo e custom fields */}
      <LoginHeader />

      {/* Layout dinâmico baseado no modelo */}
      {getLayoutComponent()}
    </div>
  )
}